import { DateTime } from "luxon";
import React, { useRef, useState } from "react";
import classNames from "classnames";
import { formaters } from "vincent-utils";
import {
  FaCheck,
  FaPause,
  FaPlay,
  FaSpinner,
  FaTrashCan,
  FaXmark,
} from "react-icons/fa6";
import { trpc } from "../../main/components/trpc.ts";

const AUDIO_NOT_READY_MESSAGE =
  "Le message audio n'a pas encore été chargé, réessayez plus tard.";
const AUDIO_PLAYBACK_ERROR_MESSAGE = "Lecture impossible pour ce message.";

interface RespondMessageCardProps {
  ownerId: string;
  message: {
    id: string;
    message: string;
    sender_number: string;
    read: boolean;
    date: Date;
    duration: number;
    name: string | null;
    org: string | null;
    patientName?: string | null;
    patientId?: string | null;
    title: string | null;
    summary: string | null;
  };
  onDelete: (messageId: string) => void;
  isDeleting: boolean;
  showDeleteAction?: boolean;
  onPatientNameClick?: () => void;
}

function secondsToMinSec(seconds: number) {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60)
    .toString()
    .padStart(2, "0");
  return `${minutes}:${remainingSeconds}`;
}

function Word(props: {
  ownIndex: number;
  currentIndex: number;
  word: string;
  setToIndex: (index: number) => void;
}) {
  return (
    <span
      className={classNames("cursor-pointer", {
        "rounded bg-teal-100 px-1 py-0.5 font-semibold text-teal-900 ring-1 ring-teal-200":
          props.ownIndex === props.currentIndex,
      })}
      onClick={() => props.setToIndex(props.ownIndex)}
    >
      {props.word}
    </span>
  );
}

function textToSpans(
  text: string,
  currentIndex: number,
  setToIndex: (index: number) => void,
) {
  const textArray = text.split(" ").flatMap((word) => {
    const splitWord = word.split("'");
    if (splitWord.length === 1) {
      return " " + splitWord;
    }
    return [" " + splitWord[0] + "'", splitWord[1]];
  });

  return textArray.map((word, index) => (
    <Word
      key={`${index}-${word}`}
      ownIndex={index}
      currentIndex={currentIndex}
      word={word}
      setToIndex={setToIndex}
    />
  ));
}

function ActionIconButton(props: {
  label: string;
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      className={classNames(
        "flex items-center justify-center text-slate-700 transition hover:text-slate-950 disabled:cursor-default disabled:opacity-60",
        props.className,
      )}
      onClick={props.onClick}
      title={props.label}
      aria-label={props.label}
      disabled={props.disabled}
    >
      {props.children}
    </button>
  );
}

export default function RespondMessageCard(props: RespondMessageCardProps) {
  const showDeleteAction = props.showDeleteAction ?? true;
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackProgress, setPlaybackProgress] = useState(0);
  const [playbackDuration, setPlaybackDuration] = useState(
    Math.max(0, props.message.duration),
  );
  const [audioSourceUrl, setAudioSourceUrl] = useState<string | null>(null);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const pendingSeekRef = useRef<number | null>(null);
  const shouldPlayWhenReadyRef = useRef(false);

  function handleAudioPlaybackError(error?: unknown) {
    console.error("Failed to play respond message audio", {
      error,
      audioSourceUrl,
      messageId: props.message.id,
      ownerId: props.ownerId,
    });
    shouldPlayWhenReadyRef.current = false;
    setAudioSourceUrl(null);
    setIsPlaying(false);
    setAudioError(AUDIO_PLAYBACK_ERROR_MESSAGE);
  }

  const words =
    trpc.respond.getMessageWordsForOwner.useQuery(props.message.id).data || [];
  const getAudioMessageMutation = trpc.respond.getAudioMessageLinkForOwner.useMutation({
    onSuccess: (nextAudioUrl) => {
      if (!nextAudioUrl) {
        pendingSeekRef.current = null;
        shouldPlayWhenReadyRef.current = false;
        setIsPlaying(false);
        setAudioError(AUDIO_NOT_READY_MESSAGE);
        return;
      }

      setAudioError(null);
      setAudioSourceUrl(nextAudioUrl);
    },
    onError: (error) => {
      handleAudioPlaybackError(error);
    },
  });

  const actualWordIndex = words.findIndex((word: (typeof words)[number]) => {
    return (
      word.word_start <= playbackProgress && word.word_end >= playbackProgress
    );
  });

  const requestAudioSource = () => {
    if (audioSourceUrl || getAudioMessageMutation.isPending) {
      return;
    }

    setAudioError(null);
    getAudioMessageMutation.mutate(props.message.id);
  };

  const applyPendingSeek = (audio: HTMLAudioElement) => {
    const pendingSeek = pendingSeekRef.current;
    if (pendingSeek == null) {
      return;
    }

    if (audio.readyState < 1) {
      return;
    }

    audio.currentTime = pendingSeek;
    setPlaybackProgress(audio.currentTime);
    pendingSeekRef.current = null;
  };

  const playAudio = (audio: HTMLAudioElement) => {
    setAudioError(null);
    setIsPlaying(true);
    void audio.play().catch((error) => {
      handleAudioPlaybackError(error);
    });
  };

  const handleListen = (second?: number) => {
    if (second != null) {
      seekToTime(second, true);
      return;
    }

    if (!audioSourceUrl) {
      shouldPlayWhenReadyRef.current = true;
      requestAudioSource();
      return;
    }

    const currentAudio = audioRef.current;
    if (!currentAudio) {
      return;
    }

    shouldPlayWhenReadyRef.current = false;
    playAudio(currentAudio);
  };

  const handleTogglePlayback = () => {
    const currentAudio = audioRef.current;
    if (!currentAudio) {
      return;
    }

    if (currentAudio.paused) {
      handleListen();
      return;
    }

    currentAudio.pause();
    setIsPlaying(false);
  };

  const seekToTime = (nextTime: number, shouldPlay = false) => {
    const currentAudio = audioRef.current;
    const safeTime = Math.max(0, nextTime);

    pendingSeekRef.current = safeTime;
    setPlaybackProgress(safeTime);

    if (!audioSourceUrl) {
      shouldPlayWhenReadyRef.current = shouldPlay;
      requestAudioSource();
      return;
    }

    if (!currentAudio) {
      return;
    }

    if (currentAudio.readyState >= 1) {
      currentAudio.currentTime = safeTime;
      setPlaybackProgress(currentAudio.currentTime);
      pendingSeekRef.current = null;
    }

    if (!shouldPlay) {
      return;
    }

    if (currentAudio.readyState >= 1) {
      shouldPlayWhenReadyRef.current = false;
      playAudio(currentAudio);
      return;
    }

    shouldPlayWhenReadyRef.current = true;
  };

  const handleSeek = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (playbackDuration <= 0) {
      return;
    }

    const bounds = event.currentTarget.getBoundingClientRect();
    const relativeX = event.clientX - bounds.left;
    const ratio = Math.min(Math.max(relativeX / bounds.width, 0), 1);
    const nextTime = ratio * playbackDuration;

    seekToTime(nextTime, !audioRef.current?.paused);
  };

  const handleSetToIndex = (index: number) => {
    const word = words[index];
    if (!word) {
      return;
    }
    seekToTime(Math.max(0, word.word_start - 0.3), true);
  };

  const wordsComps = textToSpans(
    props.message.message,
    actualWordIndex,
    handleSetToIndex,
  );
  const roundedProgress = Math.round(playbackProgress);
  const roundedDuration = Math.round(playbackDuration);
  const progressPercent =
    playbackDuration > 0
      ? Math.min(Math.max((playbackProgress / playbackDuration) * 100, 0), 100)
      : 0;
  const formattedSenderNumber =
    formaters.formatTelNumberForDisplay(props.message.sender_number) ||
    props.message.sender_number;
  const contactDetails = [
    props.message.name,
    props.message.org,
    props.message.title,
  ]
    .filter(Boolean)
    .join(" ");
  const secondaryDetails = [
    props.message.patientName ? formattedSenderNumber : null,
    contactDetails && contactDetails !== props.message.patientName
      ? contactDetails
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <article className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_10px_30px_rgba(15,23,42,0.10)]">
      <div className="flex items-center justify-between gap-4 bg-slate-950 px-4 py-3 text-white">
        <div className="min-w-0">
          <div
            className={classNames(
              "truncate text-[1.05rem] font-semibold tracking-[0.01em]",
              props.onPatientNameClick
                ? "cursor-pointer transition hover:text-blue-600"
                : null,
            )}
            onClick={props.onPatientNameClick}
          >
            {props.message.patientName || formattedSenderNumber}
          </div>
          {secondaryDetails ? (
            <div className="truncate text-xs text-slate-300">
              {secondaryDetails}
            </div>
          ) : null}
        </div>

        {!showDeleteAction ? null : props.isDeleting ? (
          <span
            className="shrink-0 text-base"
            title="Suppression en cours"
            aria-label="Suppression en cours"
          >
            <FaSpinner className="h-4 w-4 animate-spin text-slate-200" />
          </span>
        ) : showDeleteConfirmation ? (
          <div className="flex items-center gap-3">
            <ActionIconButton
              label="Annuler"
              className="shrink-0 text-slate-300 hover:text-white"
              onClick={() => setShowDeleteConfirmation(false)}
            >
              <FaXmark className="h-4 w-4" />
            </ActionIconButton>
            <ActionIconButton
              label="Confirmer la suppression"
              className="shrink-0 text-emerald-300 hover:text-emerald-100"
              onClick={() => {
                setShowDeleteConfirmation(false);
                props.onDelete(props.message.id);
              }}
            >
              <FaCheck className="h-4 w-4" />
            </ActionIconButton>
          </div>
        ) : (
          <ActionIconButton
            label="Supprimer ce message"
            className="shrink-0 text-slate-300 hover:text-white"
              onClick={() => {
                audioRef.current?.pause();
                shouldPlayWhenReadyRef.current = false;
                setIsPlaying(false);
                setShowDeleteConfirmation(true);
              }}
          >
            <FaTrashCan className="h-4 w-4" />
          </ActionIconButton>
        )}
      </div>

      <div className="space-y-4 px-4 py-4">
        <div className="text-[0.97rem] leading-7 text-slate-900">{wordsComps}</div>

        {props.message.summary ? (
          <div className="rounded-xl bg-slate-50 px-3 py-2 text-sm italic text-slate-600">
            {props.message.summary}
          </div>
        ) : null}

        {audioError ? (
          <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {audioError}
          </div>
        ) : null}

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-[280px] flex-1 items-center gap-3">
            <ActionIconButton
              label={isPlaying ? "Mettre en pause" : "Lire le message"}
              className="shrink-0 text-slate-900 hover:text-slate-700"
              onClick={handleTogglePlayback}
            >
              {isPlaying ? (
                <FaPause className="h-4 w-4" />
              ) : (
                <FaPlay className="ml-0.5 h-4 w-4" />
              )}
            </ActionIconButton>

            <div className="min-w-0 flex-1">
              <button
                type="button"
                className="relative block h-3 w-full overflow-hidden rounded-full bg-slate-200 ring-1 ring-slate-300"
                onClick={handleSeek}
                aria-label="Déplacer la lecture audio"
              >
                <span
                  className="absolute inset-y-0 left-0 rounded-full bg-teal-600"
                  style={{ width: `${progressPercent}%` }}
                />
                <span
                  className="absolute top-1/2 h-4 w-4 -translate-y-1/2 rounded-full border-2 border-white bg-slate-950 shadow"
                  style={{ left: `calc(${progressPercent}% - 0.5rem)` }}
                />
              </button>

              <div className="mt-2 flex items-center justify-between text-xs font-medium text-slate-600">
                <span>{secondsToMinSec(roundedProgress)}</span>
                <span>{secondsToMinSec(roundedDuration)}</span>
              </div>
            </div>

            <audio
              ref={audioRef}
              preload="metadata"
              className="hidden"
              src={audioSourceUrl || undefined}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onEnded={(event) => {
                shouldPlayWhenReadyRef.current = false;
                setIsPlaying(false);
                setPlaybackProgress(event.currentTarget.duration || playbackDuration);
              }}
              onLoadedData={() => setAudioError(null)}
              onLoadedMetadata={(event) => {
                setAudioError(null);
                if (Number.isFinite(event.currentTarget.duration)) {
                  setPlaybackDuration(event.currentTarget.duration);
                }
                applyPendingSeek(event.currentTarget);
              }}
              onCanPlay={(event) => {
                applyPendingSeek(event.currentTarget);

                if (shouldPlayWhenReadyRef.current) {
                  shouldPlayWhenReadyRef.current = false;
                  playAudio(event.currentTarget);
                }
              }}
              onError={(event) => {
                handleAudioPlaybackError(event.currentTarget.error);
              }}
              onTimeUpdate={(event) => {
                setPlaybackProgress(event.currentTarget.currentTime);
              }}
            />
          </div>

          <div className="text-right text-sm text-slate-500">
            <div>{DateTime.fromJSDate(new Date(props.message.date)).toFormat("dd/MM/yyyy HH:mm")}</div>
            <div className="text-xs text-slate-400">
              Durée {secondsToMinSec(roundedDuration)}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}
